import { type Feature, type Participant } from '../types';
import { DollarSign, Plus, Minus, CheckCircle2 } from 'lucide-react';

interface PurchasingViewProps {
  features: Feature[];
  participants: Participant[];
  currentParticipantIndex: number;
  onPurchase: (featureId: string, participantId: string) => void;
  onRemovePurchase: (featureId: string, participantId: string) => void;
  onNextParticipant: () => void;
  onFinish: () => void;
}

export function PurchasingView({
  features,
  participants,
  currentParticipantIndex,
  onPurchase,
  onRemovePurchase,
  onNextParticipant,
  onFinish,
}: PurchasingViewProps) {
  const currentParticipant = participants[currentParticipantIndex];
  const isLastParticipant = currentParticipantIndex === participants.length - 1;
  // This participant is done when nothing is left to spend on. A feature already
  // bought can be bought again (its Buy button stays live while it is affordable),
  // so having bought something must not keep the next step locked: with $12 left
  // and every feature at $20 there is nothing more to do.
  const currentParticipantDone =
    currentParticipant.remainingBudget === 0 ||
    features.every(f => currentParticipant.remainingBudget < f.price);

  const getPurchaseCount = (featureId: string, participantId: string) => {
    const participant = participants.find(p => p.id === participantId);
    const purchase = participant?.purchases.find(p => p.featureId === featureId);
    return purchase?.quantity || 0;
  };

  const canAfford = (feature: Feature, participant: Participant) => {
    return participant.remainingBudget >= feature.price;
  };

  const handlePurchase = (featureId: string) => {
    if (canAfford(features.find(f => f.id === featureId)!, currentParticipant)) {
      onPurchase(featureId, currentParticipant.id);
    }
  };

  const handleRemovePurchase = (featureId: string) => {
    const count = getPurchaseCount(featureId, currentParticipant.id);
    if (count > 0) {
      onRemovePurchase(featureId, currentParticipant.id);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 pb-4 border-b border-[var(--rule)]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-1">
              {currentParticipant.name}'s Turn
            </h2>
            <p className="text-sm text-[var(--text-sec)]">
              Purchase features with your budget. Click to buy, click again to remove.
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-[var(--text-pri)]">
              ${currentParticipant.remainingBudget.toFixed(2)} <span className="text-sm font-normal text-[var(--text-sec)]">remaining</span>
            </div>
            <div className="text-sm text-[var(--text-sec)]">
              ${(currentParticipant.budget - currentParticipant.remainingBudget).toFixed(2)} of ${currentParticipant.budget.toFixed(2)} spent
            </div>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-[var(--text-sec)] mb-2">
          <span>Participant {currentParticipantIndex + 1} of {participants.length}</span>
          <span>{((currentParticipantIndex + 1) / participants.length * 100).toFixed(0)}% complete</span>
        </div>
        <div className="w-full bg-[var(--hover-tint)] rounded-full h-3">
          <div
            className="bg-[var(--slate)] h-3 rounded-full transition-all duration-300"
            style={{ width: `${((currentParticipantIndex + 1) / participants.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Features Grid */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature) => {
            const purchaseCount = getPurchaseCount(feature.id, currentParticipant.id);
            const canBuy = canAfford(feature, currentParticipant);
            const hasPurchases = purchaseCount > 0;

            return (
              <div
                key={feature.id}
                className={`relative p-6  border-2 transition-all duration-200 ${
                  hasPurchases
                    ? 'border-[var(--green)] bg-[var(--green-dim)] shadow-md'
                    : canBuy
                    ? 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)] hover:shadow-sm'
                    : 'border-[var(--rule)] bg-[var(--surface)] opacity-60'
                }`}
              >
                <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
                  {feature.name}
                </h3>
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="h-5 w-5 text-[var(--text-sec)]" />
                  <span className="text-xl font-bold text-[var(--text-pri)]">${feature.price.toFixed(2)}</span>
                </div>

                {/* Purchase Count */}
                {hasPurchases && (
                  <div className="mb-4 p-2 bg-[var(--green-dim)] text-center">
                    <div className="text-2xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{purchaseCount}</div>
                    <div className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">purchased</div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePurchase(feature.id)}
                    disabled={!canBuy}
                    className={`flex-1 px-4 py-2  text-sm font-medium transition-colors ${
                      canBuy
                        ? 'bg-[var(--slate)] text-white hover:bg-[var(--slate)]'
                        : 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
                    }`}
                  >
                    <Plus className="h-4 w-4 inline mr-1" />
                    Buy
                  </button>
                  {hasPurchases && (
                    <button
                      onClick={() => handleRemovePurchase(feature.id)}
                      className="px-4 py-2 bg-[var(--coral-dim)] text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors text-sm font-medium"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {!canBuy && currentParticipant.remainingBudget > 0 && (
                  <div className="mt-2 text-xs text-[var(--text-ter)] text-center">
                    Need ${(feature.price - currentParticipant.remainingBudget).toFixed(2)} more
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="text-center">
        {isLastParticipant ? (
          <button
            onClick={onFinish}
            disabled={!currentParticipantDone}
            className={`px-8 py-3  font-medium text-lg transition-colors ${
              currentParticipantDone
                ? 'bg-[var(--green)] text-white hover:bg-[var(--green)]'
                : 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
            }`}
          >
            {currentParticipantDone ? (
              <>
                <CheckCircle2 className="h-5 w-5 inline mr-2" />
                View Results
              </>
            ) : (
              `Spend remaining $${currentParticipant.remainingBudget.toFixed(2)} or finish`
            )}
          </button>
        ) : (
          <button
            onClick={onNextParticipant}
            disabled={!currentParticipantDone}
            className={`px-8 py-3  font-medium text-lg transition-colors ${
              currentParticipantDone
                ? 'bg-[var(--slate)] text-white hover:bg-[var(--slate)]'
                : 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
            }`}
          >
            {currentParticipantDone
              ? `Next Participant (${currentParticipantIndex + 2} of ${participants.length})`
              : `Spend remaining $${currentParticipant.remainingBudget.toFixed(2)} or continue`}
          </button>
        )}
      </div>
    </div>
  );
}


